import { useRef } from 'react';

import { DeleteIcon, SearchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { SELECTED_SURFACE } from '@/components/ui/constants';
import { PREVENT_AUTOFILL_PROPS } from '@/components/ui/constants';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface SearchBarProps {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
  autoFocus?: boolean;
  'aria-label'?: string;
}

/**
 * SearchBar component with integrated search and clear functionality
 *
 * Displays a search input with a fixed search icon on the left and a dynamic
 * clear button on the right (visible only when input has value). The clear
 * button automatically focuses the input after clearing for better UX.
 *
 * @param value - Current value of the search input (controlled)
 * @param onChange - Change handler for the search input
 * @param placeholder - Placeholder text for the search input
 * @param className - Additional CSS classes to apply to the wrapper container
 */
const SearchBar = (props: SearchBarProps) => {
  const { value, onChange, className, size = 'md', ...rest } = props;
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClear = () => {
    if (onChange) {
      const event = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
    // Refocus input after clearing
    inputRef.current?.focus();
  };

  const hasValue = !!value;

  return (
    <div
      className={cn(
        'relative w-64 rounded-md border border-transparent transition-colors',
        hasValue && SELECTED_SURFACE,
        className
      )}
    >
      <SearchIcon
        className={cn(
          'absolute top-1/2 left-2 h-4 w-4 -translate-y-1/2',
          hasValue ? 'text-primary' : 'text-muted-foreground'
        )}
      />
      <Input
        ref={inputRef}
        type="search"
        size={size}
        className={cn(
          // Search fields deter password managers, but add a clear button that duplicates ours.
          'border-none bg-transparent px-8 [&::-webkit-search-cancel-button]:hidden',
          hasValue && 'font-medium text-primary'
        )}
        value={value}
        onChange={onChange}
        {...PREVENT_AUTOFILL_PROPS}
        {...rest}
      />
      {hasValue ? (
        <Button
          type="button"
          onClick={handleClear}
          aria-label={t('common.clear_search')}
          variant="ghost-neutral"
          size="icon-sm"
          className="absolute top-1/2 right-1 -translate-y-1/2 [&_svg]:text-primary"
        >
          <DeleteIcon aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
};

export default SearchBar;
